import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { BarChart3, Info, X, ArrowUpRight } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { formatTime, getFileName, formatBytes } from '@har-insight/parser';
import type { HAREntry } from '@har-insight/parser';
import { cn } from '@/lib/utils';

interface HistogramBucket {
  range: string;
  min: number;
  max: number;
  count: number;
  percentage: number;
  color: string;
  entries: HAREntry[];
}

interface PercentileData {
  p50: number;
  p75: number;
  p90: number;
  p95: number;
  p99: number;
}

export function TimingDistribution() {
  const store = useHARStore();
  const { filteredEntries, stats } = store;
  const [selectedBucket, setSelectedBucket] = useState<HistogramBucket | null>(null);

  const { histogram, percentiles } = useMemo(() => {
    if (!stats || filteredEntries.length === 0) {
      return { histogram: [], percentiles: null };
    }

    const times = filteredEntries.map(e => e.time || 0).sort((a, b) => a - b);
    const total = times.length;
    
    if (total === 0) {
      return { histogram: [], percentiles: null };
    }

    // Calculate percentiles
    const getPercentile = (p: number) => {
      const index = Math.ceil((p / 100) * total) - 1;
      return times[Math.max(0, index)] || 0;
    };

    const percentiles: PercentileData = {
      p50: getPercentile(50),
      p75: getPercentile(75),
      p90: getPercentile(90),
      p95: getPercentile(95),
      p99: getPercentile(99),
    };

    // Create histogram buckets
    const maxTime = times[times.length - 1];
    const buckets: HistogramBucket[] = [];
    
    // Define bucket ranges based on typical web performance thresholds
    const bucketRanges = [
      { max: 100, label: '< 100ms', color: 'bg-emerald-500' },
      { max: 300, label: '100-300ms', color: 'bg-emerald-400' },
      { max: 500, label: '300-500ms', color: 'bg-yellow-400' },
      { max: 1000, label: '500ms-1s', color: 'bg-yellow-500' },
      { max: 2000, label: '1-2s', color: 'bg-orange-400' },
      { max: 3000, label: '2-3s', color: 'bg-orange-500' },
      { max: 5000, label: '3-5s', color: 'bg-red-400' },
      { max: Infinity, label: '> 5s', color: 'bg-red-500' },
    ];

    let previousMax = 0;
    for (const range of bucketRanges) {
      const bucketEntries = filteredEntries.filter(e => {
        const time = e.time || 0;
        return time > previousMax && time <= range.max;
      });
      const count = bucketEntries.length;
      
      if (count > 0 || range.max <= maxTime) {
        buckets.push({
          range: range.label,
          min: previousMax,
          max: range.max === Infinity ? maxTime : range.max,
          count,
          percentage: (count / total) * 100,
          color: range.color,
          entries: bucketEntries.sort((a, b) => (b.time || 0) - (a.time || 0)),
        });
      }
      previousMax = range.max;
    }

    return { histogram: buckets, percentiles, total };
  }, [filteredEntries, stats]);

  const handleBucketClick = (bucket: HistogramBucket) => {
    if (bucket.count > 0) {
      setSelectedBucket(bucket);
    }
  };

  const handleEntryClick = (entry: HAREntry) => {
    setSelectedBucket(null);
    // Use store.getState() to get fresh reference and avoid stale closure issues
    setTimeout(() => {
      store.selectEntry(entry);
    }, 100);
  };

  const handleCloseTable = () => {
    setSelectedBucket(null);
  };

  const maxCount = Math.max(...histogram.map(b => b.count));

  if (histogram.length === 0) {
    return (
      <div className="card p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
          <BarChart3 className="w-6 h-6 text-slate-400" />
        </div>
        <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-1">No timing data</h3>
        <p className="text-xs text-slate-500">Load a HAR file to see response time distribution.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Percentiles */}
      {percentiles && (
        <div className="grid grid-cols-5 gap-2 mb-4 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
          <div className="text-center">
            <div className="text-lg font-bold text-slate-700 dark:text-slate-300">{formatTime(percentiles.p50)}</div>
            <div className="text-[10px] text-slate-500 uppercase font-medium">Median (p50)</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-slate-700 dark:text-slate-300">{formatTime(percentiles.p75)}</div>
            <div className="text-[10px] text-slate-500 uppercase font-medium">p75</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-slate-700 dark:text-slate-300">{formatTime(percentiles.p90)}</div>
            <div className="text-[10px] text-slate-500 uppercase font-medium">p90</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-slate-700 dark:text-slate-300">{formatTime(percentiles.p95)}</div>
            <div className="text-[10px] text-slate-500 uppercase font-medium">p95</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-red-600 dark:text-red-400">{formatTime(percentiles.p99)}</div>
            <div className="text-[10px] text-slate-500 uppercase font-medium">p99</div>
          </div>
        </div>
      )}

      {/* Histogram */}
      <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1">
        <div className="space-y-3">
          {histogram.map((bucket, index) => (
            <div 
              key={index} 
              className={cn(
                "space-y-1",
                bucket.count > 0 && "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/30 rounded-lg p-1 -m-1 transition-colors"
              )}
              onClick={() => handleBucketClick(bucket)}
            >
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-slate-600 dark:text-slate-400">{bucket.range}</span>
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "text-slate-500",
                    bucket.count > 0 && "group-hover:text-slate-700 dark:group-hover:text-slate-300"
                  )}>
                    {bucket.count} requests
                  </span>
                  <span className="font-bold text-slate-700 dark:text-slate-300 min-w-[40px] text-right">
                    {bucket.percentage.toFixed(1)}%
                  </span>
                </div>
              </div>
              <div className="h-6 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden">
                <div
                  className={cn('h-full transition-all duration-500 ease-out', bucket.color)}
                  style={{ width: `${Math.max((bucket.count / maxCount) * 100, 2)}%` }}
                  title={`${bucket.range}: ${bucket.count} requests (${bucket.percentage.toFixed(1)}%)`}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Performance thresholds legend */}
        <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-1.5 mb-3">
            <Info className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs font-medium text-slate-500">Performance Thresholds</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-emerald-500" />
              <span className="text-slate-600 dark:text-slate-400">Fast (&lt; 300ms)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-yellow-500" />
              <span className="text-slate-600 dark:text-slate-400">Moderate (300ms - 1s)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-orange-500" />
              <span className="text-slate-600 dark:text-slate-400">Slow (1s - 3s)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-red-500" />
              <span className="text-slate-600 dark:text-slate-400">Very Slow (&gt; 3s)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bucket Detail Modal/Overlay - rendered via portal to escape parent overflow:hidden */}
      {selectedBucket && createPortal(
        <div 
          className="fixed inset-0 z-40 flex items-start justify-center pt-20 p-4 bg-black/50"
          onClick={(e) => {
            // Close modal when clicking backdrop
            if (e.target === e.currentTarget) {
              handleCloseTable();
            }
          }}
        >
          <div 
            className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-3xl max-h-[60vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {selectedBucket.range}
                </h3>
                <p className="text-sm text-slate-500">
                  {selectedBucket.count} requests ({selectedBucket.percentage.toFixed(1)}%)
                </p>
              </div>
              <button
                onClick={handleCloseTable}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Requests Table */}
            <div className="flex-1 overflow-auto p-4">
              <div className="space-y-2">
                {selectedBucket.entries.map((entry, index) => (
                  <button
                    key={entry._id || index}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleEntryClick(entry);
                    }}
                    className={cn(
                      "w-full text-left p-3 rounded-lg border transition-all cursor-pointer",
                      "hover:shadow-md hover:-translate-y-0.5",
                      "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700",
                      "hover:border-slate-300 dark:hover:border-slate-600",
                      "group"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      {/* Status Code */}
                      <span className={cn(
                        "text-xs font-bold px-2 py-1 rounded flex-shrink-0",
                        entry.response.status >= 200 && entry.response.status < 300 && "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400",
                        entry.response.status >= 300 && entry.response.status < 400 && "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400",
                        entry.response.status >= 400 && "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400"
                      )}>
                        {entry.response.status}
                      </span>
                      
                      {/* Method */}
                      <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 w-12 flex-shrink-0">
                        {entry.request.method}
                      </span>
                      
                      {/* URL */}
                      <span className="text-sm text-slate-700 dark:text-slate-300 truncate flex-1 font-medium">
                        {getFileName(entry.request.url)}
                      </span>
                      
                      {/* Time */}
                      <span className={cn(
                        "text-xs font-bold flex-shrink-0",
                        (entry.time || 0) > 1000 ? "text-orange-600 dark:text-orange-400" : "text-slate-600 dark:text-slate-400"
                      )}>
                        {formatTime(entry.time || 0)}
                      </span>
                      
                      {/* Size */}
                      <span className="text-xs text-slate-500 flex-shrink-0 w-16 text-right">
                        {formatBytes(entry.response.content.size || 0)}
                      </span>
                      
                      {/* Arrow */}
                      <ArrowUpRight className="w-4 h-4 text-slate-400 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 rounded-b-xl">
              <p className="text-xs text-slate-500 text-center">
                Click any request to view details in the sidebar
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
